import { useWorkspaceText } from './interface-copy';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { ConnectionPolicyFields } from './connect-access/policy-fields';
import type { ConnectionReadItem } from './connection-read-access/api';
import { CredentialsForm, SuccessState } from './native-postgres-setup/parts';
import { TablePicker } from './native-postgres-setup/table-picker';
import type { NativePostgresConfig } from './native-postgres-setup/model';
import { useNativePostgresSetup } from './native-postgres-setup/use-native-postgres-setup';
import type { PostgresEntry } from './pipedream-catalogue-api';
import { CatalogueCard } from './catalogue-card';
import { useNamespaceCopy } from '@/i18n/ns';
import pageCatalog from '@/i18n/ns/connections-page';
import nativeCopyCatalog from '@/i18n/ns/native-postgres';

type SetupProps = {
  open: boolean;
  onClose: () => void;
  companyName: string;
  canCompany: boolean;
  scopeKey?: string;
  onSaved?: (item: ConnectionReadItem) => void;
  onDeleted?: (notice?: string) => void;
  onTryInChat?: (prompt: string) => void;
  existing?: ConnectionReadItem | null;
  initialConfig?: Partial<NativePostgresConfig>;
  /** Who a new connection is for until the access step changes it. */
  initialAudience?: 'private' | 'company';
};

export function NativePostgresCard({
  entry,
  busy = false,
  onOpen,
}: {
  entry: PostgresEntry;
  /** Another connect is under way: the tile waits like every other. */
  busy?: boolean;
  onOpen: (audience: 'private' | 'company') => void;
}) {
  const copy = useNamespaceCopy(pageCatalog);
  const { setup } = entry;
  const unavailable = !setup.available || !setup.canManage;
  return (
    <CatalogueCard
      source="poneva"
      logo={entry.img_src}
      name={entry.name}
      subtitle={
        setup.reason === 'company_admin_required' ? copy.tileAdminSetup : entry.categories?.[0]
      }
      description={entry.description}
      state={unavailable ? 'unavailable' : 'connect'}
      action={{
        disabled: unavailable || busy,
        // Setup chooses who can use the database; an Owner or Admin can always choose the company.
        onClick: () => onOpen('private'),
        label: unavailable ? copy.tileUnavailable : copy.tileConnect,
      }}
    />
  );
}

function NativePostgresSetupContent({
  open,
  onClose,
  companyName,
  canCompany,
  scopeKey = '',
  onSaved,
  onDeleted,
  onTryInChat,
  existing,
  initialConfig,
  initialAudience,
}: SetupProps) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const nativeCopy = useNamespaceCopy(nativeCopyCatalog);
  const state = useNativePostgresSetup({
    open,
    scopeKey,
    existing,
    initialConfig,
    initialAudience,
    onClose,
    onSaved,
    onDeleted,
  });
  const title =
    state.step === 'credentials'
      ? existing
        ? nativeCopy.manageTitle
        : nativeCopy.connectTitle
      : state.step === 'tables'
        ? nativeCopy.chooseTablesTitle
        : state.step === 'access'
          ? nativeCopy.chooseAccessTitle
          : state.grantState === 'enabled'
            ? nativeCopy.connectedTitle
            : nativeCopy.savedTitle;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="pc-portal ws-pg-dialog">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {state.step === 'credentials'
            ? uiText(
                'Connect securely, choose the tables to share, then decide whether Poneva can read or make changes.',
              )
            : state.step === 'tables'
              ? existing
                ? uiText('Review the tables this connection can access. Select at least one table.')
                : uiText('Select at least one table. Nothing is selected by default.')
              : state.step === 'access'
                ? uiText('Choose who can use this connection and what Poneva can do.')
                : state.grantState === 'enabled'
                  ? uiText('Your chosen access is enabled for the selected tables.')
                  : uiText(
                      'Your connection is saved with reading paused until permission is confirmed.',
                    )}
        </DialogDescription>
        {state.error && (
          <p className="ws-pg-error" role="alert">
            {state.error}
          </p>
        )}
        {state.confirmRemove && (
          <div
            className="ws-pg-confirm"
            role="alertdialog"
            aria-label={uiText('Confirm database disconnect')}
          >
            <strong>{uiText('Disconnect this database?')}</strong>
            <p>{uiText('Access will stop for everyone using this connection.')}</p>
            <Button variant="outline" onClick={() => state.setConfirmRemove(false)}>
              {uiText('Keep connection')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                state.setConfirmRemove(false);
                void state.remove();
              }}
            >
              {uiText('Disconnect')}
            </Button>
          </div>
        )}
        {state.step === 'credentials' && (
          <CredentialsForm
            config={state.config}
            advanced={state.advanced}
            existing={Boolean(existing)}
            busy={state.busy}
            loadingExisting={state.loadingExisting}
            onChange={state.updateConfig}
            onAdvanced={() => state.setAdvanced(!state.advanced)}
            onTest={() => void state.testConnection()}
            onRemove={() => state.setConfirmRemove(true)}
            valid={state.validCredentials}
          />
        )}
        {state.step === 'tables' && (
          <TablePicker
            tables={state.tables}
            onSelect={state.select}
            selected={state.selected}
            query={state.query}
            checks={state.tested?.checks}
            onQuery={state.setQuery}
            onToggle={state.toggle}
            disabled={state.busy}
          />
        )}
        {state.step === 'access' && (
          <div className="ws-pg-audience">
            <ConnectionPolicyFields
              value={state.supportsWrites ? state.choice : { ...state.choice, changes: 'ask' }}
              onChange={state.setChoice}
              companyName={companyName}
              canCompany={canCompany}
              ownerUserId={null}
              people={state.people}
              writeUnavailableReason={
                !state.supportsWrites
                  ? state.tested?.checks.readOnly === 'replica'
                    ? nativeCopy.readOnlyReplica
                    : nativeCopy.readOnlyAccount
                  : undefined
              }
              disabled={state.busy}
            />
            <details className="ws-pg-table-scope">
              <summary>
                {(state.selected.length === 1
                  ? nativeCopy.tablesReachOne
                  : nativeCopy.tablesReachMany
                ).replace('{count}', String(state.selected.length))}
              </summary>
              <ul>
                {state.selected.map((resource) => (
                  <li key={resource}>{resource}</li>
                ))}
              </ul>
            </details>
          </div>
        )}
        {state.step === 'saved' && (
          <SuccessState
            enabled={state.grantState === 'enabled'}
            writeAllowed={state.supportsWrites && state.choice.changes === 'allowed'}
            grantError={state.grantError}
            busy={state.busy}
            onRetry={() => void state.retryGrant()}
            onRemove={() => state.setConfirmRemove(true)}
            onTryInChat={onTryInChat}
            onDone={onClose}
          />
        )}
        {(state.step === 'tables' || state.step === 'access') && (
          <footer className="ws-pg-actions">
            <Button variant="outline" disabled={state.busy} onClick={() => state.back()}>
              {uiText('Back')}
            </Button>
            <Button
              onClick={() =>
                state.step === 'tables' ? state.continueToAccess() : void state.save()
              }
              disabled={
                !state.selected.length ||
                state.busy ||
                (state.step === 'access' &&
                  state.choice.audience === 'people' &&
                  !state.choice.people.length)
              }
            >
              {state.step === 'tables'
                ? uiText('Continue')
                : state.busy
                  ? uiText('Saving…')
                  : `${existing ? 'Save access' : 'Connect'} (${state.selected.length})`}
            </Button>
          </footer>
        )}
      </DialogContent>
    </Dialog>
  );
}

// A company or account change drops all entered credentials and in-flight UI state.
export function NativePostgresSetup(props: SetupProps) {
  return <NativePostgresSetupContent key={props.scopeKey ?? ''} {...props} />;
}
